import { useCallback, useEffect, useState } from "react";
import { CalendarClock, ChevronRight, Zap } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { eur, INVOICE_STATUS } from "@/components/accounting/docUtils";

const fmtDay = (iso) => {
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
};

function rowStatus(row) {
  if (!row.invoice) return { label: "À générer", cls: "bg-neutral-100 text-neutral-500 border-neutral-200" };
  const st = INVOICE_STATUS[row.invoice.status] || INVOICE_STATUS.brouillon;
  if (row.invoice.status === "brouillon") return { label: "Brouillon à valider", cls: st.cls };
  return { label: st.label, cls: st.cls };
}

export default function AutoBillingPanel({ variant = "full", onOpenInvoice, onSeeAll }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/accounting/auto-billing-overview").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async (row) => {
    setBusy(row.client_id);
    try {
      await api.post(`/accounting/clients/${row.client_id}/generate-monthly-invoice`);
      toast.success(`Brouillon créé pour ${row.client_name} — à valider dans l'onglet Factures`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  if (!data) return null;
  if (data.clients.length === 0) {
    if (variant !== "full") return null;
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-10 text-center" data-testid="auto-billing-empty">
        <CalendarClock className="w-8 h-8 text-neutral-300 mx-auto mb-2" />
        <p className="text-sm font-semibold text-neutral-600">Aucun client en facturation automatique</p>
        <p className="text-xs text-neutral-400 mt-1">Activez la facture mensuelle automatique depuis la fiche d'un client (section Facturation).</p>
      </div>
    );
  }

  const toValidate = data.clients.filter((c) => c.invoice?.status === "brouillon");
  const toGenerate = data.clients.filter((c) => !c.invoice);

  if (variant === "compact") {
    return (
      <div className="bg-blue-50/60 border border-blue-100 rounded-xl px-4 py-3" data-testid="auto-billing-compact">
        <div className="flex items-center gap-3 flex-wrap">
          <CalendarClock className="w-4 h-4 text-[#1D4ED8] shrink-0" />
          <p className="text-xs text-neutral-700">
            <span className="font-semibold" data-testid="auto-compact-next-gen">Prochaine génération automatique : le {fmtDay(data.next_generation)}</span>
            <span className="text-neutral-500"> — {data.clients.length} client(s) en facturation auto</span>
          </p>
          <span className="flex items-center gap-1.5 ml-auto">
            {toValidate.length > 0 && (
              <span className="text-[10px] font-semibold bg-white text-blue-700 border border-blue-200 rounded-full px-2 py-0.5" data-testid="auto-compact-to-validate">
                {toValidate.length} brouillon(s) à valider
              </span>
            )}
            {toGenerate.length > 0 && (
              <span className="text-[10px] font-semibold bg-white text-neutral-500 border border-neutral-200 rounded-full px-2 py-0.5" data-testid="auto-compact-to-generate">
                {toGenerate.length} à générer
              </span>
            )}
            <button onClick={onSeeAll} className="text-[11px] font-semibold text-[#0369A1] hover:underline inline-flex items-center" data-testid="auto-compact-see-all">
              Vue d'ensemble <ChevronRight className="w-3 h-3" />
            </button>
          </span>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
          {data.clients.map((c) => {
            const st = rowStatus(c);
            return (
              <span key={c.client_id} className="inline-flex items-center gap-1.5 text-[11px] text-neutral-600" data-testid={`auto-compact-row-${c.client_id}`}>
                <span className="font-medium">{c.client_name}</span>
                <span className={`text-[9px] font-semibold border rounded-full px-1.5 py-px ${st.cls}`}>{st.label}</span>
              </span>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden" data-testid="auto-billing-panel">
      <div className="px-5 py-4 border-b border-neutral-100 flex items-center gap-3 flex-wrap">
        <span className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
          <CalendarClock className="w-5 h-5 text-[#1D4ED8]" />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading font-bold text-sm" data-testid="auto-next-gen">
            Prochaine génération automatique : le {fmtDay(data.next_generation)}
          </h2>
          <p className="text-[11px] text-neutral-400">
            Les brouillons sont créés le {data.draft_day} de chaque mois — aucune facture ne part sans votre validation.
          </p>
        </div>
        {toValidate.length > 0 && (
          <span className="text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2.5 py-1" data-testid="auto-count-to-validate">
            {toValidate.length} brouillon(s) à valider
          </span>
        )}
      </div>
      <div className="divide-y divide-neutral-50">
        {data.clients.map((c) => {
          const st = rowStatus(c);
          return (
            <div key={c.client_id} className="flex items-center gap-3 px-5 py-3" data-testid={`auto-row-${c.client_id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-neutral-800 truncate">{c.client_name}</p>
                <p className="text-[10px] text-neutral-400">
                  Envoi prévu le {c.send_day} du mois{c.expected_ttc != null ? ` · ${eur(c.expected_ttc)}` : ""}
                </p>
              </div>
              {c.invoice && (
                <p className="text-xs font-semibold text-neutral-700 whitespace-nowrap" data-testid={`auto-amount-${c.client_id}`}>
                  {eur(c.invoice.total_ttc)}
                </p>
              )}
              <span className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 whitespace-nowrap ${st.cls}`} data-testid={`auto-status-${c.client_id}`}>
                {st.label}
              </span>
              {!c.invoice ? (
                <button onClick={() => generate(c)} disabled={busy === c.client_id} data-testid={`auto-generate-${c.client_id}`}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold bg-[#0B132B] text-white rounded-full px-3 py-1.5 hover:bg-[#1C2A4F] disabled:opacity-50 transition-colors">
                  <Zap className="w-3 h-3" /> {busy === c.client_id ? "Création…" : "Générer maintenant"}
                </button>
              ) : (
                <button onClick={() => onOpenInvoice?.(c.invoice.id)} data-testid={`auto-open-${c.client_id}`}
                  className="text-[11px] font-semibold text-[#0369A1] hover:underline whitespace-nowrap">
                  Ouvrir la facture
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
